import React, { useMemo, useState, useCallback } from "react";
import SmartForm, { Field } from "@insightfulscience/smart-react/SmartForm";
import Textarea from "@insightfulscience/atomic-react/Textarea";
import { useLocale } from "@insightfulscience/smart-react/i18";
import { func, node, object } from "prop-types";
import Select from "@insightfulscience/atomic-react/Select";
import Input from "@insightfulscience/atomic-react/Input";
import { compose } from "@insightfulscience/shared-utils/fn";
import { productOfInterest, userTypes, validateForm } from "./form";
import { useCountries, useCountryStates } from "../countries";

const generateOptions = (start, stop, step = 1) =>
	Array.from({ length: (stop - start) / step + 1 }, (_, key) => ({
		text: start + key * step,
		value: start + key * step,
	}));

const QuoteFormFields = ({ children, action, initialState }) => {
	const [t] = useLocale("quote-form");

	const eventToNumber = ({ target }) => +target.value;
	const [countryID, setCountryID] = useState(initialState.countryID);
	const handleCountryChange = useMemo(
		() => compose(setCountryID, eventToNumber), // callback
		[setCountryID],
	);

	const [countries, countryById] = useCountries();
	const states = useCountryStates(countryID);
	const setCountry = useCallback(
		(form, chosenCountry) => ({
			...form,
			countryID: +chosenCountry,
			address1: countryById.get(+chosenCountry)?.name,
			state: "",
		}),
		[],
	);

	const setNumberOfLicenses = useCallback(
		(form, numberOfLicenses) => ({
			...form,
			numberOfLicenses: +numberOfLicenses,
		}),
		[],
	);

	return (
		<SmartForm {...{ validateForm, action }} initialState={initialState} textRender={t}>
			<Field
				fieldClassName="mb-8"
				name="firstName"
				label="FIRST_NAME"
				required
				maxLength={50}
				data-qa-selector="firstName-field"
			/>

			<Field
				fieldClassName="mb-8"
				name="lastName"
				label="LAST_NAME"
				required
				maxLength={50}
				data-qa-selector="lastName-field"
			/>

			<Field
				fieldClassName="mb-8"
				name="email"
				label="EMAIL"
				required
				maxLength={100}
				data-qa-selector="email-field"
			/>

			<Field
				fieldClassName="mb-8"
				name="company"
				label="COMPANY"
				required
				maxLength={50}
				data-qa-selector="company-field"
			/>

			<Field
				fieldClassName="mb-8"
				name="countryID"
				label="COUNTRY"
				required
				Control={Select}
				parseEvent={eventToNumber}
				onChange={handleCountryChange}
				setState={setCountry}
				data-qa-selector="country-select-field"
			>
				<Select.Placeholder>{t("COUNTRY_PLACEHOLDER")}</Select.Placeholder>
				{Select.renderOptions(
					countries,
					({ id }) => id,
					({ name }) => name,
				)}
			</Field>

			<Field
				fieldClassName="mb-8"
				name="state"
				label="STATE"
				Control={states ? Select : Input}
				required={!!states}
				maxLength={states ? undefined : 50}
				data-qa-selector="state-field"
			>
				{states && (
					<>
						<Select.Placeholder>{t("STATE_PLACEHOLDER")}</Select.Placeholder>
						{Select.renderOptions(
							states,
							({ isoAlpha2 }) => isoAlpha2,
							({ isoName }) => isoName,
						)}
					</>
				)}
			</Field>

			<Field
				fieldClassName="mb-8"
				name="productOfInterest"
				label="PRODUCT_OF_INTEREST"
				required
				Control={Select}
				data-qa-selector="product-of-interest-select-field"
			>
				<Select.Placeholder>{t("PRODUCT_OF_INTEREST_PLACEHOLDER")}</Select.Placeholder>
				{Select.renderOptions(
					productOfInterest,
					({ value }) => value,
					({ text }) => t(text),
				)}
			</Field>

			<Field
				fieldClassName="mb-8"
				name="userType"
				label="USER_TYPE"
				required
				Control={Select}
				data-qa-selector="user-type-select-field"
			>
				<Select.Placeholder>{t("USER_TYPE_PLACEHOLDER")}</Select.Placeholder>
				{Select.renderOptions(
					userTypes,
					({ value }) => value,
					({ text }) => t(text),
				)}
			</Field>

			<Field
				fieldClassName="mb-8"
				name="numberOfLicenses"
				label="NUMBER_OF_ACTIVATIONS"
				required
				setState={setNumberOfLicenses}
				Control={Select}
				data-qa-selector="number-of-licenses-select-field"
			>
				<Select.Placeholder>{t("NUMBER_OF_ACTIVATIONS_PLACEHOLDER")}</Select.Placeholder>
				{Select.renderOptions(
					generateOptions(1, 50),
					({ value }) => value,
					({ text }) => text,
				)}
			</Field>

			<Field
				fieldClassName="mb-8"
				Control={Textarea}
				name="message"
				label="MESSAGE"
				maxLength={400}
				data-qa-selector="message-field"
			/>
			{children}
		</SmartForm>
	);
};

QuoteFormFields.propTypes = {
	initialState: object, // eslint-disable-line react/forbid-prop-types
	action: func,
	children: node,
};

export default QuoteFormFields;
